import { useState, useCallback, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, Package, Cpu, FileCode2, Plus, Trash2, Upload, X, AlertCircle, File, ChevronDown, ChevronRight
} from 'lucide-react'
import clsx from 'clsx'
import { useApplication } from '../../hooks/useApplications'
import {
  useTemplates, useCreateTemplate, useUpdateTemplate, useDeleteTemplate,
  useTemplateFiles, useUploadTemplateFiles, useDeleteTemplateFile
} from '../../hooks/useTemplates'
import { useRole } from '../../hooks/useRole'

export default function ApplicationDetail() {
  const { t } = useTranslation()
  const { applicationId } = useParams()
  const navigate = useNavigate()
  const { data: application, isLoading, error } = useApplication(applicationId)
  const { data: templates, isLoading: templatesLoading } = useTemplates(applicationId)
  const createTemplate = useCreateTemplate(applicationId)
  const deleteTemplate = useDeleteTemplate(applicationId)
  const { canDelete } = useRole()

  const [activeTab, setActiveTab] = useState('templates')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newTemplateName, setNewTemplateName] = useState('')
  const [newTemplateDesc, setNewTemplateDesc] = useState('')
  const [newTemplateUtc, setNewTemplateUtc] = useState(true)
  const [expandedTemplate, setExpandedTemplate] = useState(null)

  const handleCreateTemplate = async (e) => {
    e.preventDefault()
    if (!newTemplateName.trim()) return
    try {
      await createTemplate.mutateAsync({
        name: newTemplateName.trim(),
        type: 'webui',
        description: newTemplateDesc.trim() || null,
        webUiTelemetryTimesAreUtc: newTemplateUtc,
      })
      setShowCreateModal(false)
      setNewTemplateName('')
      setNewTemplateDesc('')
      setNewTemplateUtc(true)
    } catch (err) {
      alert(err?.response?.data?.message || t('applications.detail.createError'))
    }
  }

  const handleDeleteTemplate = async (id, name) => {
    if (window.confirm(t('applications.detail.deleteConfirm', { name }))) {
      try {
        await deleteTemplate.mutateAsync(id)
        if (expandedTemplate === id) setExpandedTemplate(null)
      } catch (err) {
        alert(t('applications.detail.deleteError'))
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">{t('common.generic.loading')}</div>
      </div>
    )
  }

  if (error || !application) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {t('applications.detail.notFound')}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/applications')} className="btn btn-secondary btn-sm">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3">
          <div className="icon-box icon-box-lg" style={{ background: 'var(--theme-icon-box-background, linear-gradient(145deg, var(--theme-gradient-start), var(--theme-gradient-mid)))' , color: 'var(--theme-icon-box-text, #fff)' }}>
            <Package className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{application.name}</h1>
            <p className="text-sm text-gray-600">{application.description || t('applications.detail.noDescription')}</p>
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          <button
            type="button"
            onClick={() => setActiveTab('templates')}
            className={clsx(
              'pb-3 text-sm font-medium border-b-2 transition-colors inline-flex items-center',
              activeTab === 'templates'
                ? 'tab-active-secondary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <FileCode2 className={clsx('w-4 h-4 inline mr-2', activeTab === 'templates' ? 'text-theme-secondary' : 'text-gray-400')} />
            {t('applications.detail.tabTemplates')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('devices')}
            className={clsx(
              'pb-3 text-sm font-medium border-b-2 transition-colors inline-flex items-center',
              activeTab === 'devices'
                ? 'tab-active-secondary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <Cpu className={clsx('w-4 h-4 inline mr-2', activeTab === 'devices' ? 'text-theme-secondary' : 'text-gray-400')} />
            {t('applications.detail.tabDevices', { count: application.deviceCount || 0 })}
          </button>
        </nav>
      </div>

      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">{t('applications.detail.templatesTitle')}</h2>
            <button onClick={() => setShowCreateModal(true)} className="btn btn-primary btn-sm">
              <Plus className="w-4 h-4" />
              {t('applications.detail.newTemplate')}
            </button>
          </div>

          {templatesLoading ? (
            <div className="text-gray-600 py-8 text-center">{t('applications.detail.loadingTemplates')}</div>
          ) : !templates?.length ? (
            <div className="card p-12 text-center">
              <FileCode2 className="w-12 h-12 text-theme-secondary mx-auto mb-4 opacity-90" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('applications.detail.emptyTemplates')}</h3>
              <p className="text-gray-600 mb-4">{t('applications.detail.emptyTemplatesHint')}</p>
              <button onClick={() => setShowCreateModal(true)} className="btn btn-primary btn-sm">
                <Plus className="w-4 h-4" />
                {t('applications.detail.newTemplate')}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((tpl) => (
                <div key={tpl.id} className="card">
                  <div
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-t-xl"
                    onClick={() => setExpandedTemplate(expandedTemplate === tpl.id ? null : tpl.id)}
                  >
                    <div className="flex items-center gap-3">
                      {expandedTemplate === tpl.id
                        ? <ChevronDown className="w-4 h-4 text-gray-400" />
                        : <ChevronRight className="w-4 h-4 text-gray-400" />
                      }
                      <FileCode2 className="w-5 h-5 text-theme-secondary shrink-0" />
                      <div>
                        <span className="font-medium text-gray-900">{tpl.name}</span>
                        <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{tpl.type}</span>
                        {tpl.description && (
                          <p className="text-sm text-gray-500 mt-0.5">{tpl.description}</p>
                        )}
                      </div>
                    </div>
                    {canDelete && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(tpl.id, tpl.name) }}
                        className="btn btn-error btn-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {expandedTemplate === tpl.id && (
                    <div className="border-t border-gray-200 p-4 space-y-4">
                      <TemplateUtcRow template={tpl} applicationId={applicationId} />
                      <TemplateFilesPanel templateId={tpl.id} applicationId={applicationId} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'devices' && (
        <div className="card p-8 text-center text-gray-500">
          <Cpu className="w-10 h-10 mx-auto mb-3 text-gray-400" />
          <p>{t('applications.detail.devicesCount', { count: application.deviceCount || 0 })}</p>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('applications.detail.newTemplate')}</h3>
              <button onClick={() => setShowCreateModal(false)}>
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            <form onSubmit={handleCreateTemplate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('applications.detail.nameLabel')}</label>
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  className="input w-full"
                  placeholder={t('applications.detail.namePlaceholder')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('applications.detail.descriptionLabel')}</label>
                <input
                  type="text"
                  value={newTemplateDesc}
                  onChange={(e) => setNewTemplateDesc(e.target.value)}
                  className="input w-full"
                  placeholder={t('applications.detail.descriptionPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('applications.detail.typeLabel')}</label>
                <select className="input w-full" disabled>
                  <option>webui</option>
                </select>
              </div>
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-gray-300"
                  checked={newTemplateUtc}
                  onChange={(e) => setNewTemplateUtc(e.target.checked)}
                />
                <span>
                  <span className="font-medium">{t('applications.detail.utcLabel')}</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {t('applications.detail.utcHintCreate')}
                  </span>
                </span>
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                  {t('common.buttons.cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={createTemplate.isPending}>
                  {createTemplate.isPending ? t('applications.detail.creating') : t('applications.detail.createTemplate')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

function TemplateUtcRow({ template, applicationId }) {
  const { t } = useTranslation()
  const updateTemplate = useUpdateTemplate(applicationId)
  const serverUtc = template.webUiTelemetryTimesAreUtc !== false
  const [utc, setUtc] = useState(serverUtc)

  useEffect(() => {
    setUtc(template.webUiTelemetryTimesAreUtc !== false)
  }, [template.id, template.webUiTelemetryTimesAreUtc])

  const handleUtcChange = async (e) => {
    const next = e.target.checked
    const prev = utc
    setUtc(next)
    try {
      await updateTemplate.mutateAsync({
        id: template.id,
        data: { webUiTelemetryTimesAreUtc: next },
      })
    } catch (err) {
      setUtc(prev)
      alert(err?.response?.data?.message || t('applications.detail.utcSaveError'))
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3">
      <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 rounded border-gray-300"
          checked={utc}
          onChange={handleUtcChange}
          disabled={updateTemplate.isPending}
        />
        <span>
          <span className="font-medium">{t('applications.detail.utcLabel')}</span>
          <span className="block text-xs text-gray-500 mt-0.5">
            {t('applications.detail.utcHintEdit')}
          </span>
        </span>
      </label>
    </div>
  )
}

function TemplateFilesPanel({ templateId, applicationId }) {
  const { t } = useTranslation()
  const { data: filesData, isLoading } = useTemplateFiles(templateId)
  const uploadFiles = useUploadTemplateFiles(templateId, applicationId)
  const deleteFile = useDeleteTemplateFile(templateId)
  const { canDelete } = useRole()
  const [dragOver, setDragOver] = useState(false)

  const files = filesData?.files || []

  const handleFileDrop = useCallback(async (e) => {
    e.preventDefault()
    setDragOver(false)
    const items = e.dataTransfer?.files
    if (items?.length) {
      try {
        await uploadFiles.mutateAsync(Array.from(items))
      } catch (err) {
        alert(t('applications.detail.uploadError'))
      }
    }
  }, [uploadFiles, t])

  const handleFileSelect = useCallback(async (e) => {
    const items = e.target.files
    if (items?.length) {
      try {
        await uploadFiles.mutateAsync(Array.from(items))
      } catch (err) {
        alert(t('applications.detail.uploadError'))
      }
    }
    e.target.value = ''
  }, [uploadFiles, t])

  const handleDeleteFile = async (filename) => {
    if (window.confirm(t('applications.detail.deleteFileConfirm', { filename }))) {
      try {
        await deleteFile.mutateAsync(filename)
      } catch (err) {
        alert(t('applications.detail.deleteFileError'))
      }
    }
  }

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="space-y-3">
      <div
        className={clsx(
          'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
          dragOver ? 'border-theme-primary bg-theme-primary/10' : 'border-gray-300 hover:border-gray-400',
          uploadFiles.isPending && 'opacity-50 pointer-events-none'
        )}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleFileDrop}
      >
        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600 mb-2">
          {uploadFiles.isPending ? t('applications.detail.uploading') : t('applications.detail.dropHere')}
        </p>
        <label className="btn btn-secondary btn-sm cursor-pointer">
          {t('applications.detail.selectFiles')}
          <input type="file" multiple className="hidden" onChange={handleFileSelect} />
        </label>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">{t('applications.detail.loadingFiles')}</p>
      ) : files.length === 0 ? (
        <p className="text-sm text-gray-500">{t('applications.detail.noFiles')}</p>
      ) : (
        <div className="space-y-1">
          {files.map((f) => (
            <div key={f.name} className="flex items-center justify-between py-2 px-3 rounded hover:bg-gray-50">
              <div className="flex items-center gap-2 min-w-0">
                <File className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-sm text-gray-800 truncate">{f.name}</span>
                <span className="text-xs text-gray-400 shrink-0">{formatSize(f.size)}</span>
              </div>
              {canDelete && (
                <button
                  onClick={() => handleDeleteFile(f.name)}
                  className="text-gray-400 hover:text-red-500 shrink-0 ml-2"
                  disabled={deleteFile.isPending}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
